import { useState } from 'react';

// Image card with Aceternity UI effects - spotlight and hover animations
const ImageCard = ({ image, onImageClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Track mouse position for spotlight effect
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <div
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onImageClick(image)}
      className="relative overflow-hidden rounded-xl cursor-pointer group bg-gray-100 dark:bg-gray-900 aspect-square shadow-md hover:shadow-xl transition-all duration-300"
    >
      {/* Spotlight effect overlay */}
      {isHovered && (
        <div
          className="pointer-events-none absolute -inset-px rounded-xl transition duration-300"
          style={{
            background: `radial-gradient(circle 600px at ${position.x}px ${position.y}px, rgba(59, 130, 246, 0.15), transparent 80%)`,
            opacity: 1,
          }}
        />
      )}

      {/* Border glow */}
      <div
        className={`absolute -inset-px rounded-xl transition duration-300 pointer-events-none ${
          isHovered
            ? 'bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20'
            : 'bg-gradient-to-r from-transparent via-transparent to-transparent'
        }`}
      />

      {/* Image */}
      <img
        src={image.urls.small}
        alt={image.alt_description || 'Gallery image'}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
      />

      {/* Dark overlay on hover */}
      <div
        className={`absolute inset-0 transition-all duration-300 ${
          isHovered ? 'bg-black/30' : 'bg-black/0'
        }`}
      />

      {/* Title overlay - bottom gradient (always visible) */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 sm:p-4 z-10">
        <p className="text-white text-xs sm:text-sm font-medium line-clamp-2">
          {image.alt_description || 'Untitled'}
        </p>
      </div>

      {/* Content overlay - photographer info on hover */}
      <div
        className={`absolute inset-0 flex flex-col justify-start p-3 sm:p-4 transition-all duration-300 z-20 pointer-events-none ${
          isHovered ? 'opacity-100 pointer-events-auto' : 'opacity-0'
        }`}
      >
        {/* Top section - photographer info only */}
        <div className="flex items-center gap-2 bg-gradient-to-r from-black/50 to-transparent px-3 py-2 rounded-lg w-fit">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
            {image.user?.name?.charAt(0) || '?'}
          </div>
          <div className="text-white text-xs sm:text-sm font-medium truncate">
            {image.user?.name || 'Unknown'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCard;
